import { defineStore } from 'pinia'
import dayjs from "~/plugins/dayjs";

const SCHEMA_CONTEXT = 'https://schema.org'

type BreadcrumbItem = {
  title: string
  href: string
}

type ProductForJsonLd = {
  title: string
  startDate?: string | null
  startTime?: string | null
  endDate?: string | null
  endTime?: string | null
  seoDescription?: string | null
  media?: { url_large?: string | null }[] | null
  venue: {
    name?: string | null
    address?: string | null
    timezone?: string | null
  }
  startPrice?: number | null
  url?: string | null
  competitions?: { name: string }[] | null
}

type SiteForJsonLd = {
  data?: {
    currency?: {
      code?: string | null
    } | null
  } | null
}

export function withJsonLdContext(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(withJsonLdContext)
  }

  if (!value || typeof value !== 'object') {
    return value
  }

  const normalized = Object.fromEntries(
      Object.entries(value).map(([key, nestedValue]) => [key, withJsonLdContext(nestedValue)])
  )

  if ('@type' in normalized && typeof normalized['@context'] !== 'string') {
    normalized['@context'] = SCHEMA_CONTEXT
  }

  return normalized
}

type State = {
  container: unknown[],
}

export const useJsonLdStore = defineStore('jsonld', {
  state: (): State => ({
    container: [],
  }),
  actions: {
    addBreadcrumbs(items: BreadcrumbItem[]) {
      if (import.meta.client) return

      const currentUrl = useRequestURL();

      this.container.push(withJsonLdContext({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        "itemListElement": items.map((item, index) => {
          return {
            "@type": "ListItem",
            "position": index + 1,
            "name": item.title,
            "item": currentUrl.origin + item.href
          }
        }),
      }));
    },
    addSportEvent(product: ProductForJsonLd, site: SiteForJsonLd) {
      if (import.meta.client) return

      const currentUrl = useRequestURL();

      const productTeams = product.title.split(/ vs?\.? /);

      const productStart = product.startDate ? dayjs.tz(
          `${product.startDate} ${product.startTime ? product.startTime : '00:00:00'}`,
          product?.venue?.timezone || 'UTC'
      ) : null;
      const productEnd = product.endDate ? dayjs.tz(
          `${product.endDate} ${product.endTime ? product.endTime : '00:00:00'}`,
          product?.venue?.timezone || 'UTC'
      ) : null;

      const sportEvent: Record<string, unknown> = {
        '@context': 'https://schema.org',
        '@type': 'SportsEvent',
        startDate: productStart ? productStart.toISOString() : '',
        endDate: productEnd ? productEnd.toISOString() : '',
        name: product.title,
        description: product.seoDescription ?? '',
        image: product.media ? product.media[0]?.url_large : null,
        eventStatus: productStart ? 'EventScheduled' : 'EventPostponed',
        awayTeam: {
          "@type": "Thing",
          name: productTeams[1] ?? product.title,
        },
        homeTeam: {
          "@type": "Thing",
          name: productTeams[0] ?? product.title,
        },
        location: {
          "@type": "Place",
          "name": product.venue.name,
          "address": {
            "@type": "PostalAddress",
            "streetAddress": product.venue.address,
          },
        },
        "performer": [
          {
            "@type": "Organization",
            "name": productTeams[0] ?? ""
          },
          {
            "@type": "Organization",
            "name": productTeams[1] ?? ""
          }
        ]
      };
      if (product.startPrice) {
        sportEvent.offers = {
          "@type": "Offer",
          "availability": "https://schema.org/InStock",
          "price": product.startPrice,
          "priceCurrency": useCurrencyStore().code ?? site.data?.currency?.code ?? '',
          "url": `${currentUrl.origin}/${product.url}`,
        };
      }
      else {
        sportEvent.offers = {
          "@type": "Offer",
          "availability": "https://schema.org/OutOfStock",
          "url": `${currentUrl.origin}/${product.url}`,
        };
      }
      if (product.competitions && product.competitions.length) {
        sportEvent.organizer = {
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": product.competitions[0].name
        };
      }
      this.container.push(withJsonLdContext(sportEvent));
    },

  },
  getters: {
    jsonLd(state): unknown[] {
      return state.container;
    },
  },
})
